/**
 * Users & access.
 *
 * Identity comes from Cloudflare Access (one-time PIN); this page controls what a
 * verified identity is allowed to do. Anyone who signs in successfully but is not
 * yet listed is recorded as an inactive viewer for an owner to approve — they
 * cannot read data until activated.
 *
 * Granting access here also adds the address to the Cloudflare Access group that
 * the application's policy includes, so this page is the only place a user has to
 * be added — any address, not just one on the organisation's domain. The table
 * flags any row Access would still turn away, because a list that can drift
 * silently is what made this necessary in the first place.
 */
import { useState } from 'react'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import { EnumSelect } from '../components/Select'
import {
  Card, Field, Badge, Modal, Switch, Spinner, Notice, QueryState,
  ConfirmModal, useToast, fmtInt, fmtDateTime,
} from '../components/ui'

const CAPS = [
  ['can_read', 'Read'],
  ['can_create', 'Create'],
  ['can_update', 'Edit'],
  ['can_delete', 'Delete'],
  ['can_use_ai', 'Use AI'],
  ['can_upload_media', 'Upload images'],
  ['can_manage_settings', 'Manage settings'],
  ['can_manage_users', 'Manage users'],
]

/**
 * One side of what a re-sync will change. Addresses arrive as a prop on purpose: a
 * confirmation's body is relabelled for the installation's term, the walk leaves an
 * element's props alone, and an address is the one thing here that must stay exactly as
 * it is.
 */
function ResyncList({ label, note, emails }) {
  if (!emails.length) return null
  return (
    <div>
      <div className="small"><strong>{label} ({emails.length})</strong></div>
      <div className="small subtle">{note}</div>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18, maxHeight: 180, overflowY: 'auto' }}>
        {emails.map((e) => (
          <li key={e} className="mono small">{e}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Re-sync Access asks first, because it is the one button here that changes who can
 * reach the app in bulk: it admits every active account and drops everything else, on a
 * single press. The list is refetched as this opens, so what is shown is what Access
 * holds now. Unreadable means nothing can be shown, so nothing can be confirmed.
 */
function ResyncConfirm({ open, onClose, onConfirm, busy, checking, failed, access, admit, drop }) {
  const readable = !!access?.emails && !failed
  const unchanged = readable && !admit.length && !drop.length
  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      busy={busy}
      confirmDisabled={checking || !readable || unchanged}
      confirmLabel="Re-sync Access"
      title="Re-sync Cloudflare Access?"
      body={
        checking ? (
          <p>Checking what Cloudflare Access holds now…</p>
        ) : !readable ? (
          <Notice tone="danger" icon="warning" title="Could not read Cloudflare Access">
            <div className="small">Nothing can be shown, so a re-sync cannot be confirmed yet.</div>
            {access?.error && <div className="small mono">{access.error}</div>}
          </Notice>
        ) : unchanged ? (
          <p>Cloudflare Access already matches the active accounts in this table. Nothing would change.</p>
        ) : (
          <div className="col">
            <p>This makes Cloudflare Access match the active accounts in this table exactly. Nobody is emailed.</p>
            <ResyncList
              label="Will be let in"
              note="They can reach the app straight away."
              emails={admit}
            />
            <ResyncList
              label="Will be removed"
              note="They can no longer reach the sign-in page. Their Zero Trust seat is freed unless another S4CA app still lists them."
              emails={drop}
            />
          </div>
        )
      }
    />
  )
}

export default function SettingsUsers({ user }) {
  const query = useApi('users', 'users', { enabled: user.caps.manageUsers })
  const toast = useToast()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('viewer')
  const [busy, setBusy] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [confirmSync, setConfirmSync] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [editing, setEditing] = useState(null) // { row, email, name }

  if (!user.caps.manageUsers) {
    return (
      <PageHeader title="Users & Access">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Only owners can manage users. You are signed in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const invite = async () => {
    setBusy(true)
    try {
      const res = await api('users', {
        method: 'POST',
        body: { email, display_name: name, role_key: role, is_active: true },
      })
      toast.ok(
        res?.accessSynced
          ? `${email} can now sign in`
          : `${email} is authorized here, but Access sync is off — add them in Zero Trust`
      )
      setInviteOpen(false)
      setEmail('')
      setName('')
      setRole('viewer')
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  /*
   * The switch is a door change as well as an in-app one: off takes the address out of
   * Cloudflare Access and frees the seat, on puts it back. So it says what happened at the
   * door, which is the part nothing else on the page would show.
   */
  const reportSwitch = (row, on, res) => {
    if (res?.accessSynced === false) {
      toast.info(`Access sync is off, so Cloudflare Access was not told about ${row.email}. Change it in Zero Trust by hand.`)
    } else if (on) {
      toast.ok(`${row.email} is switched on and can sign in`)
    } else if (res?.seat?.released) {
      toast.ok(`${row.email} is switched off, and their Zero Trust seat freed`)
    } else if (res?.seat?.shared) {
      toast.ok(`${row.email} is switched off. Their Zero Trust seat stays, since another S4CA app still lists them`)
    } else {
      toast.ok(`${row.email} is switched off and can no longer reach the sign-in page`)
    }
    if (res?.seat?.error) toast.error(`Could not free any Zero Trust seat ${row.email} holds: ${res.seat.error}`)
  }

  const update = async (row, patch) => {
    try {
      const res = await api(`users/${row.id}`, { method: 'PUT', body: patch })
      if ('is_active' in patch) reportSwitch(row, patch.is_active, res)
      query.refetch()
    } catch (err) {
      toast.error(err)
    }
  }

  const saveEdit = async () => {
    const { row } = editing
    const email = editing.email.trim().toLowerCase()
    setBusy(true)
    try {
      const res = await api(`users/${row.id}`, {
        method: 'PUT',
        body: { email, display_name: editing.name },
      })
      if (email !== row.email) {
        toast.ok(
          res?.accessSynced
            ? `${email} can now sign in in place of ${row.email}`
            : `Changed to ${email} here, but Access sync is off — update them in Zero Trust`
        )
      } else {
        toast.ok('Saved')
      }
      // Separate toasts for the same reason as a removal: the change happened, and
      // the leftover is a second fact nothing else on the page would explain.
      if (res?.accessError) {
        toast.error(`${row.email} is still allowed by Cloudflare Access: ${res.accessError}. Re-sync Access to remove it.`)
      }
      if (res?.seat?.error) toast.error(`Could not free any Zero Trust seat ${row.email} holds: ${res.seat.error}`)
      setEditing(null)
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const openResync = () => {
    setConfirmSync(true)
    query.refetch()
  }

  const resync = async () => {
    setSyncing(true)
    try {
      const res = await api('users/sync', { method: 'POST' })
      const freed = res.seats?.released?.length || 0
      toast.ok(
        `Cloudflare Access now matches these ${res.count} addresses` +
          (freed ? `, and ${freed} Zero Trust seat${freed === 1 ? '' : 's'} freed` : '')
      )
      if (res.seats?.error) {
        toast.error(`The seats held by ${res.dropped.join(', ')} could not be freed: ${res.seats.error}`)
      }
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setSyncing(false)
      setConfirmSync(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      const res = await api(`users/${confirmDelete.id}`, { method: 'DELETE' })
      toast.ok(
        res?.seat?.released
          ? 'Access removed, and their Zero Trust seat freed'
          : res?.seat?.shared
            ? 'Access removed. Their Zero Trust seat stays, since another S4CA app still lists them'
            : 'Access removed'
      )
      // Two toasts rather than one hedged sentence: the removal did happen, and the
      // seat is a separate fact that nothing else on this page would show.
      if (res?.seat?.error) toast.error(`Could not free any Zero Trust seat they hold: ${res.seat.error}`)
      setConfirmDelete(null)
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <PageHeader
      title="Users & Access"
      sub="Cloudflare Access verifies who someone is; this page decides what they may do"
      actions={
        <>
          {query.data?.access?.configured && (
            <button className="btn btn-sm" onClick={openResync} disabled={syncing}
              title="See what would change, then make Cloudflare Access match this table exactly">
              {syncing ? <Spinner /> : 'Re-sync Access'}
            </button>
          )}
          <button className="btn btn-sm btn-primary" onClick={() => setInviteOpen(true)}>
            + Grant Access
          </button>
        </>
      }
    >
      <QueryState query={query}>
        {(d) => {
          // Only an account nobody has acted on is waiting for anyone. One an owner switched
          // off is just off, and is shown that way in its row.
          const pending = d.rows.filter((r) => r.awaiting_approval)
          // The group holds the active accounts, so drift is measured against those alone.
          const active = d.rows.filter((r) => r.is_active)
          const inAccess = d.access?.emails && new Set(d.access.emails)
          const missing = inAccess ? active.filter((r) => !inAccess.has(r.email)) : []
          const strangers = inAccess
            ? d.access.emails.filter((e) => !active.some((r) => r.email === e))
            : []
          return (
            <div className="col">
              <Notice icon="lock" title="How sign-in works">
                The application sits behind Cloudflare Zero Trust with the one-time PIN provider.
                Access emails a <strong>six-digit code</strong> — not a link — to verify the address,
                then forwards a signed token that this Worker verifies against your team&apos;s public
                keys. Granting access below adds the address to the Access group the policy allows,
                so any address works, on your domain or not. Switching someone off takes the address out
                of that group again and frees their Zero Trust seat, unless another S4CA app still lists
                them. An email that reaches the Worker without a row here is recorded as inactive and
                cannot read anything until an owner activates it.
              </Notice>

              {!d.access?.configured && (
                <Notice tone="warn" icon="warning" title="Access sync is not configured">
                  Addresses granted here are authorized inside the app but are <em>not</em> added to
                  Cloudflare Access, so new people still cannot sign in. Set the{' '}
                  <code>CF_API_TOKEN</code> secret to turn this on.
                </Notice>
              )}

              {d.access?.error && (
                <Notice tone="danger" icon="warning" title="Could not read Cloudflare Access">
                  <div className="small mono">{d.access.error}</div>
                </Notice>
              )}

              {missing.length ? (
                <Notice tone="warn" icon="block" title={`${missing.length} active here but blocked by Access`}>
                  {missing.map((r) => r.email).join(', ')} cannot reach the app at all. Re-sync Access
                  to add them.
                </Notice>
              ) : null}

              {strangers.length ? (
                <Notice tone="warn" icon="person" title={`${strangers.length} allowed by Access but not an active account here`}>
                  {strangers.join(', ')} can reach the sign-in page but cannot use the app. Re-sync Access
                  to remove them and free any Zero Trust seat they are taking up.
                </Notice>
              ) : null}

              {pending.length ? (
                <Notice tone="warn" icon="pending" title={`${pending.length} account awaiting approval`}>
                  {pending.map((p) => p.email).join(', ')} signed in but have no access level yet.
                </Notice>
              ) : null}

              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Active</th>
                      <th>Last seen</th>
                      <th className="right">Actions logged</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => {
                      const isSelf = r.email === user.email
                      return (
                        <tr key={r.id}>
                          <td>
                            <span className="mono small">{r.email}</span>
                            {isSelf && (
                              <Badge tone="accent" style={{ marginLeft: 6 }}>
                                you
                              </Badge>
                            )}
                            {!r.is_active && (
                              <Badge
                                style={{ marginLeft: 6 }}
                                title={
                                  r.awaiting_approval
                                    ? 'Signed in on their own; no owner has acted on it yet'
                                    : 'Switched off by an owner. Not in Cloudflare Access'
                                }
                              >
                                {r.awaiting_approval ? 'awaiting approval' : 'switched off'}
                              </Badge>
                            )}
                            {inAccess && !!r.is_active && !inAccess.has(r.email) && (
                              <Badge
                                tone="danger"
                                style={{ marginLeft: 6 }}
                                title="Cloudflare Access will not let this address reach the app"
                              >
                                blocked by Access
                              </Badge>
                            )}
                          </td>
                          <td>{r.display_name ?? <span className="subtle">—</span>}</td>
                          <td style={{ minWidth: 190 }}>
                            <EnumSelect
                              value={r.role_key}
                              onChange={(v) => v && update(r, { role_key: v })}
                              options={d.roles.map((x) => ({ value: x.key, label: x.label }))}
                              isClearable={false}
                              placeholder="Role"
                            />
                          </td>
                          <td>
                            <Switch
                              checked={!!r.is_active}
                              onChange={(on) => update(r, { is_active: on })}
                              disabled={isSelf}
                              title={isSelf ? 'You cannot deactivate your own account' : undefined}
                            />
                          </td>
                          <td className="small nowrap">
                            {r.last_seen_at ? fmtDateTime(r.last_seen_at) : <span className="subtle">never</span>}
                          </td>
                          <td className="num">{fmtInt(r.action_count)}</td>
                          <td className="nowrap">
                            <button
                              className="btn btn-sm"
                              onClick={() => setEditing({ row: r, email: r.email, name: r.display_name ?? '' })}
                            >
                              Edit
                            </button>
                            {!isSelf && (
                              <button
                                className="btn btn-sm btn-danger"
                                style={{ marginLeft: 6 }}
                                onClick={() => setConfirmDelete(r)}
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <Card title="What Each Role Can Do">
                <div className="table-wrap flush">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>Role</th>
                        {CAPS.map(([, label]) => (
                          <th key={label} className="center">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.roles.map((r) => (
                        <tr key={r.key}>
                          <td>
                            <strong>{r.label}</strong>
                            <div className="tiny subtle mono">{r.key}</div>
                          </td>
                          {CAPS.map(([key]) => (
                            <td key={key} className="center">
                              {r[key] ? (
                                <span style={{ color: 'var(--ok)' }}>✓</span>
                              ) : (
                                <span className="subtle">—</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="small subtle" style={{ marginTop: 10 }}>
                  The last active owner cannot be demoted or removed, and nobody can change their
                  own role — both are enforced by the API, not just hidden in this UI.
                </p>
              </Card>

              <Modal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                title="Grant access"
                footer={
                  <>
                    <button className="btn btn-primary" onClick={invite} disabled={busy || !email.includes('@')}>
                      {busy ? <Spinner /> : 'Grant access'}
                    </button>
                    <button className="btn" onClick={() => setInviteOpen(false)}>
                      Cancel
                    </button>
                  </>
                }
              >
                <div className="col">
                  <Notice icon="info">
                    {d.access?.configured
                      ? 'This authorizes the email in the app and adds it to Cloudflare Access, so ' +
                        'they can sign in straight away — any address, on your domain or not. Nothing ' +
                        'is emailed to them: send them the link yourself and Access will PIN the address.'
                      : 'Access sync is off, so this only authorizes the email inside the app. Until ' +
                        'you add the address to your Cloudflare Access policy by hand, sign-in never ' +
                        'reaches the Worker.'}
                  </Notice>
                  <Field label="Email" required>
                    <input
                      className="input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="someone@silkroaders.org"
                    />
                  </Field>
                  <Field label="Display name">
                    <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
                  </Field>
                  <Field label="Role" required>
                    <EnumSelect
                      value={role}
                      onChange={(v) => setRole(v || 'viewer')}
                      options={d.roles.map((x) => ({ value: x.key, label: x.label }))}
                      isClearable={false}
                    />
                  </Field>
                </div>
              </Modal>

              <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={`Edit ${editing?.row.email}`}
                footer={
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={saveEdit}
                      disabled={busy || !editing?.email.includes('@')}
                    >
                      {busy ? <Spinner /> : 'Save'}
                    </button>
                    <button className="btn" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </>
                }
              >
                {editing && (
                  <div className="col">
                    <Field
                      label="Email"
                      required
                      hint={
                        editing.row.email === user.email
                          ? 'You cannot change your own address — your session was issued for it. Ask another owner.'
                          : undefined
                      }
                    >
                      <input
                        className="input"
                        type="email"
                        value={editing.email}
                        disabled={editing.row.email === user.email}
                        onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                      />
                    </Field>
                    <Field label="Display name">
                      <input
                        className="input"
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    </Field>
                    {editing.email.trim().toLowerCase() !== editing.row.email && (
                      <Notice tone="warn" icon="info">
                        {d.access?.configured
                          ? `The new address is added to Cloudflare Access and ${editing.row.email} is ` +
                            'removed, with any Zero Trust seat it held. They sign in with the new address ' +
                            'from now on; their favorites move with them, and past activity stays ' +
                            'recorded under the old one.'
                          : 'Access sync is off, so this only changes the address inside the app. Until ' +
                            'the new address is added to your Cloudflare Access policy by hand, they ' +
                            'cannot sign in with it.'}
                      </Notice>
                    )}
                  </div>
                )}
              </Modal>

              <ResyncConfirm
                open={confirmSync}
                onClose={() => setConfirmSync(false)}
                onConfirm={resync}
                busy={syncing}
                checking={query.isFetching}
                failed={query.isError}
                access={d.access}
                admit={missing.map((r) => r.email)}
                drop={strangers}
              />

              <ConfirmModal
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={remove}
                busy={busy}
                confirmLabel="Remove access"
                title={`Remove ${confirmDelete?.email}?`}
                body={
                  <p>
                    They will lose access immediately.{' '}
                    {d.access?.configured
                      ? 'Their address is also removed from Cloudflare Access, so they can no longer reach the sign-in page at all, and the Zero Trust seat they were taking up is freed with it. You will be told if that second part fails — it needs a token permission the group sync does not.'
                      : 'They remain allowed by your Cloudflare Access policy, so they can still reach the sign-in page and will reappear here as an inactive viewer awaiting approval.'}
                  </p>
                }
              />
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
